/**
 * Share grants as fixture data (W9 §C.5, §A.3).
 *
 * Three grants: an active view link, an active comment link behind a password, and a revoked
 * link. `read()` never throws — it answers the same discriminated `status` union the real
 * `*.viaGrant` actions do, so a dead or gated link is data, not an exception (§E-8). `update`/
 * `revoke` on an id this fixture does not track throw `NotFoundError`, mirroring the server's
 * deliberately non-leaking 404 for an unknown, foreign-team or unmanageable grant alike — a
 * component must never say "you may not revoke this," only "that link no longer exists."
 */
import { ForbiddenError, NotFoundError } from "~/api/errors";
import type {
  GrantComment,
  GrantRead,
  ShareGrant,
  ShareGrantDraft,
  ShareGrantPatch,
} from "~/api/types";

const BASE_TIME = Date.parse("2026-08-12T09:00:00.000Z");
const daysAgo = (d: number) => new Date(BASE_TIME - d * 86_400_000).toISOString();

interface GrantRow extends ShareGrant {
  password: string | null;
}

const SEED: GrantRow[] = [
  {
    id: "grant-1",
    documentId: "doc-1",
    urlId: "grant-view-1",
    url: "https://desk.docit.pro/p/grant-view-1",
    linkType: "View",
    recipientContactId: null,
    recipientEmail: null,
    expiresAt: null,
    hasPassword: false,
    revokedAt: null,
    active: true,
    views: 12,
    lastAccessedAt: daysAgo(1),
    createdAt: daysAgo(20),
    password: null,
  },
  {
    id: "grant-2",
    documentId: "doc-2",
    urlId: "grant-comment-1",
    url: "https://desk.docit.pro/p/grant-comment-1",
    linkType: "Comment",
    recipientContactId: null,
    recipientEmail: "reviewer@example.com",
    expiresAt: new Date(BASE_TIME + 30 * 86_400_000).toISOString(),
    hasPassword: true,
    revokedAt: null,
    active: true,
    views: 3,
    lastAccessedAt: daysAgo(0.2),
    createdAt: daysAgo(5),
    password: "secret123",
  },
  {
    id: "grant-3",
    documentId: "doc-1",
    urlId: "grant-revoked-1",
    url: "https://desk.docit.pro/p/grant-revoked-1",
    linkType: "View",
    recipientContactId: null,
    recipientEmail: null,
    expiresAt: null,
    hasPassword: false,
    revokedAt: daysAgo(2),
    active: false,
    views: 40,
    lastAccessedAt: daysAgo(3),
    createdAt: daysAgo(60),
    password: null,
  },
];

export interface FakeGrantDocument {
  id: string;
  title: string;
  text: string;
  emoji: string | null;
  updatedAt: string | null;
}

export interface FakeGrantsDeps {
  requireSession(): unknown;
  delay<T>(value: T): Promise<T>;
  canShareDocument(documentId: string): boolean;
  documentExists(documentId: string): boolean;
  /** Author-withheld projection of the document a grant resolves to. */
  documentFor(documentId: string): FakeGrantDocument | null;
}

export function createFakeGrants(deps: FakeGrantsDeps) {
  let grants = SEED.map((grant) => ({ ...grant }));
  const anonymousComments = new Map<string, GrantComment[]>();

  const stripPassword = (row: GrantRow): ShareGrant => {
    const { password: _password, ...rest } = row;
    return rest;
  };

  const findManageable = (id: string): GrantRow => {
    const found = grants.find((grant) => grant.id === id);
    // An unknown id, a foreign-team grant, and an in-team grant the caller may not manage all
    // answer the SAME 404 server-side (§A.3.2) — a fixture that told them apart would let a
    // "you don't have permission" string ship where the server never sends one.
    if (!found) {
      throw new NotFoundError("That link no longer exists.");
    }
    return found;
  };

  const findByUrlId = (urlId: string): GrantRow | undefined =>
    grants.find((g) => g.urlId === urlId);

  return {
    list: async (documentId: string): Promise<ShareGrant[]> => {
      deps.requireSession();
      if (!deps.canShareDocument(documentId)) {
        throw new ForbiddenError("You cannot manage sharing on this document");
      }
      return deps.delay(
        grants.filter((grant) => grant.documentId === documentId).map(stripPassword),
      );
    },

    create: async (input: ShareGrantDraft): Promise<ShareGrant> => {
      deps.requireSession();
      if (!deps.documentExists(input.documentId)) {
        throw new NotFoundError("Document not found");
      }
      if (!deps.canShareDocument(input.documentId)) {
        throw new ForbiddenError("You cannot share this document");
      }
      const urlId = `grant-${Math.random().toString(36).slice(2, 10)}`;
      const row: GrantRow = {
        id: `grant-${grants.length + 1}`,
        documentId: input.documentId,
        urlId,
        url: `https://desk.docit.pro/p/${urlId}`,
        linkType:
          input.linkType === "comment" ? "Comment" : input.linkType === "edit" ? "Edit" : "View",
        recipientContactId: input.recipientContactId ?? null,
        recipientEmail: input.recipientEmail ?? null,
        expiresAt: input.expiresAt ?? null,
        hasPassword: Boolean(input.password),
        revokedAt: null,
        active: true,
        views: 0,
        lastAccessedAt: null,
        createdAt: new Date(BASE_TIME).toISOString(),
        password: input.password ?? null,
      };
      grants = [...grants, row];
      return deps.delay(stripPassword(row));
    },

    update: async (input: ShareGrantPatch & { id: string }): Promise<ShareGrant> => {
      deps.requireSession();
      const found = findManageable(input.id);
      const updated: GrantRow = {
        ...found,
        ...(input.linkType
          ? {
              linkType:
                input.linkType === "comment"
                  ? "Comment"
                  : input.linkType === "edit"
                    ? "Edit"
                    : "View",
            }
          : {}),
        ...(input.clearExpiry
          ? { expiresAt: null }
          : input.expiresAt
            ? { expiresAt: input.expiresAt }
            : {}),
        ...(input.clearPassword
          ? { hasPassword: false, password: null }
          : input.password
            ? { hasPassword: true, password: input.password }
            : {}),
      };
      grants = grants.map((grant) => (grant.id === updated.id ? updated : grant));
      return deps.delay(stripPassword(updated));
    },

    revoke: async (id: string): Promise<void> => {
      deps.requireSession();
      const found = findManageable(id);
      const updated: GrantRow = {
        ...found,
        active: false,
        revokedAt: new Date(BASE_TIME).toISOString(),
      };
      grants = grants.map((grant) => (grant.id === id ? updated : grant));
      return deps.delay(undefined);
    },

    read: async (input: { urlId: string; password?: string }): Promise<GrantRead> => {
      // Deliberately no `requireSession()` — this is the anonymous door.
      const grant = findByUrlId(input.urlId);
      if (!grant?.active) {
        return { status: "gone" };
      }
      if (grant.hasPassword && grant.password !== input.password) {
        return { status: "password_required", invalid: input.password !== undefined };
      }
      const document = deps.documentFor(grant.documentId);
      if (!document) {
        return { status: "gone" };
      }
      return {
        status: "ok",
        grantId: grant.id,
        linkType: grant.linkType,
        permission: grant.linkType.toLowerCase(),
        document,
      };
    },

    comments: async (input: { urlId: string; password?: string }): Promise<GrantComment[]> => {
      const grant = findByUrlId(input.urlId);
      if (!grant?.active || (grant.hasPassword && grant.password !== input.password)) {
        return [];
      }
      return anonymousComments.get(grant.urlId) ?? [];
    },

    comment: async (input: {
      urlId: string;
      password?: string;
      authorName?: string;
      text: string;
    }): Promise<GrantComment> => {
      const grant = findByUrlId(input.urlId);
      if (!grant?.active) {
        throw new NotFoundError("This link is no longer active.");
      }
      if (grant.hasPassword && grant.password !== input.password) {
        throw new ForbiddenError("Password required.");
      }
      if (grant.linkType === "View") {
        throw new ForbiddenError("This link does not allow commenting.");
      }
      const comment: GrantComment = {
        id: `grant-comment-${Math.random().toString(36).slice(2, 8)}`,
        data: {
          type: "doc",
          content: [{ type: "paragraph", content: [{ type: "text", text: input.text }] }],
        },
        author: input.authorName || "A guest",
        isAnonymous: true,
        createdAt: new Date(BASE_TIME).toISOString(),
      };
      anonymousComments.set(grant.urlId, [...(anonymousComments.get(grant.urlId) ?? []), comment]);
      return comment;
    },
  };
}
